import { motion } from "framer-motion";
import { ArrowUpRight, ArrowUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TESTS } from "../lib/judges";

/**
 * The stand. Three cards laid at different angles that straighten and lift under
 * the cursor — used both on the pale panel below the World and inside the corner
 * pill's popup, so a judge meets the same physical object either way.
 */
const TILT = [-2.4, 1.1, 2.6];

export const JudgesCards = ({ compact = false, onNavigate }) => {
  const nav = useNavigate();

  const go = (to) => {
    onNavigate?.();
    if (to === "/") return window.scrollTo({ top: 0, behavior: "smooth" });
    nav(to);
  };

  return (
    <div className="flex flex-col items-stretch gap-6 md:flex-row md:items-start md:gap-0">
      {TESTS.map((t, i) => (
        <motion.article
          key={t.n}
          data-testid={`judges-card-${i + 1}`}
          className="relative flex-1 md:[&:not(:first-child)]:-ml-5"
          style={{ zIndex: 10 + i }}
          initial={{ opacity: 0, y: 34, rotate: TILT[i] }}
          whileInView={{ opacity: 1, y: 0, rotate: TILT[i] }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.75, delay: i * 0.12, ease: [0.16, 0.84, 0.24, 1] }}
          whileHover={{ rotate: 0, y: -14, scale: 1.035, zIndex: 40 }}
        >
          <div
            className={`flex h-full flex-col gap-4 border border-[rgba(36,18,6,0.16)] ${
              compact ? "px-5 pb-6 pt-5" : "px-7 pb-8 pt-7"
            } ${i < TESTS.length - 1 ? "md:pr-14" : ""}`}
            style={{
              background: "linear-gradient(168deg, #fffdf5 0%, #fdf6e4 58%, #f6ead0 100%)",
              boxShadow:
                "0 34px 60px -26px rgba(58,28,4,0.55), 0 4px 0 0 rgba(255,255,255,0.6) inset",
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <p className="mono pt-2 text-[10px] uppercase tracking-[0.24em] text-[#8a4a00]">
                {t.n}
                <br />
                <span className="text-[rgba(36,18,6,0.45)]">{t.where}</span>
              </p>
              <span
                className="engrave select-none font-bold leading-none text-[#8a4a00]"
                style={{ opacity: 0.13, fontSize: compact ? 46 : 64 }}
                aria-hidden
              >
                {i + 1}
              </span>
            </div>

            <h3
              className={`engrave font-semibold leading-[1.2] tracking-[0.03em] text-[#241206] ${
                compact ? "text-[18px]" : "text-[22px]"
              }`}
            >
              {t.title}
            </h3>
            <p
              className={`leading-[1.6] text-[#412510] ${
                compact ? "text-[14.5px]" : "text-[17px]"
              }`}
            >
              {t.body}
            </p>
            <p
              className={`mt-auto border-t border-[rgba(36,18,6,0.14)] pt-4 leading-[1.55] text-[#6d4526] ${
                compact ? "text-[14px]" : "text-[16px]"
              }`}
            >
              <span className="mono text-[10px] uppercase tracking-[0.2em] text-[#8a4a00]">
                Expect
              </span>
              <br />
              {t.expect}
            </p>
            <button
              type="button"
              data-testid={`judges-light-cta-${i + 1}`}
              onClick={() => go(t.to)}
              className="mono flex items-center gap-2 self-start rounded-full border border-[rgba(138,74,0,0.5)] px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[#8a4a00] transition-colors duration-300 hover:border-[#241206] hover:bg-[#241206] hover:text-[#f6f1e4]"
            >
              {t.cta}
              {t.to === "/" ? <ArrowUp size={12} /> : <ArrowUpRight size={12} />}
            </button>
          </div>
        </motion.article>
      ))}
    </div>
  );
};

export default JudgesCards;
